/* eslint-disable react/no-unsafe -- moved an old file */
/**
 * A basic table that displays the data provided to it and lets the user
 * sort that data by column. Fully responsive across all screen widths.
 *
 * Assumed the width of table content is determined entirely
 * by props + window width.
 */

import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {mediaQueries} from "@khan/shared-styles";

import Column from "./column.ts";
import {TableBodyCell, TableHeaderCell} from "./table-cells.tsx";
import type {
    ColumnDescriptor,
    CommonTableProps,
    DescriptorId,
    Row,
    RowHeight,
    Sort,
} from "./types.ts";
import {cellPadding, transformColumnProps} from "./utils.ts";

type SortableTableProps = CommonTableProps & {
    // Components describing how to render each column. Note: rowHeight from
    // CommonTableProps sets the minHeight of the rows, but rows may be taller
    // to fit content. This is done automatically by the HTML table element if
    // there is no frozen column, and manually if there is.
    children?:
        | Array<React.ReactElement<React.ComponentProps<typeof Column>>>
        | React.ReactElement<React.ComponentProps<typeof Column>>
        | Column;
    // Maximum number of rows to display in the table (after sorting).
    maxRows?: number;
    // Set pageIndex and pageSize to enable pagination.
    pageIndex?: number;
    pageSize?: number;
    rowStyles?: (rowIndex: number) => StyleType;
    // Whether to keep the top row in place while sorting.
    hasFixedTopRow?: boolean;
};

interface SortableTableState {
    // The id of the column that is currently being sorted.
    activeSortColumn: DescriptorId | null | undefined;
    // The direction of the column sort that is currently active.
    activeSortDirection: Sort | null | undefined;
    // The index of the currently hovered row.
    hoveredRow: number | null | undefined;
    // The current padding between columns (excluding cell padding).
    paddingBetweenColumns: number;
    // The current padding between the last column and the table's right edge.
    paddingToRightEdge: number;
    // Whether to render a frozen first column, with the other columns
    // scrolling underneath it. This is done when the screen is too narrow
    // to fit all of the table's content.
    freezeFirstColumn: boolean;
    // The heights of individual rows. Since SortableTable uses two tables to
    // freeze the first column if the SortableTable is too wide for the screen,
    // the heights of corresponding rows between the two tables may need to be
    // adjusted.
    rowHeights: Array<number> | null | undefined;
    headingRowHeight: number | null | undefined;
}

interface TableRowProps {
    row: Row;
    rowIndex: number;
    rowHeight?: number;
    hoveredRow: number | null | undefined;
    onRowClick?: (row?: Row, idx?: number) => unknown;
    isRowDisabled?: (row?: Row, idx?: number) => boolean;
    useCursorPointerOnHover?: boolean;
    columns: Array<ColumnDescriptor>;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    cells: Array<Array<HTMLDivElement>>;
    paddingBetweenColumns: number;
    paddingToRightEdge: number;
    hideFirstColumn: boolean;
    emptyRow?: boolean;
    rows: Array<Array<HTMLTableRowElement>>;
    // The parent table can choose to alternate rows between white (default)
    // and offwhite backgrounds. This prop controls which style this row should
    // take.
    backgroundStyle?: "offwhite" | "default";
    rowStyle?: StyleType;
    topAlignCellContent?: boolean;
}

const maxPaddingBetweenColumns = 210 - 2 * cellPadding;

// eslint-disable-next-line react-refresh/only-export-components -- moved an old file
const TableRow = (props: TableRowProps) => {
    const {
        row,
        rowIndex,
        rowHeight,
        hoveredRow,
        onRowClick,
        isRowDisabled,
        useCursorPointerOnHover,
        columns,
        onMouseEnter,
        onMouseLeave,
        cells,
        paddingBetweenColumns,
        paddingToRightEdge,
        hideFirstColumn,
        backgroundStyle,
        emptyRow,
        rows,
        topAlignCellContent,
        rowStyle,
    } = props;

    const rowDisabled = isRowDisabled ? isRowDisabled(row, rowIndex) : false;

    return (
        <tr
            ref={(tr) => {
                const tableIndex = hideFirstColumn ? 1 : 0;
                rows[tableIndex] = rows[tableIndex] || [];
                // @ts-expect-error - TS2322 - Type 'HTMLTableRowElement | null' is not assignable to type 'HTMLTableRowElement'.
                rows[tableIndex][rowIndex] = tr;
            }}
            key={rowIndex}
            className={css(
                styles.tr,
                onRowClick &&
                    !rowDisabled &&
                    hoveredRow === rowIndex &&
                    styles.hoveredRow,
                onRowClick &&
                    !rowDisabled &&
                    useCursorPointerOnHover &&
                    styles.hoverWithPointer,
                row.isSelected && styles.selectedRow,
                backgroundStyle === "offwhite" && styles.offWhiteBackground,
            )}
            onClick={
                onRowClick && !rowDisabled
                    ? () => onRowClick(row, rowIndex)
                    : undefined
            }
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            {columns.map((column, columnIndex) => (
                <td
                    key={columnIndex}
                    style={{
                        textAlign: column.align,
                        paddingInlineEnd:
                            (columnIndex < columns.length - 1
                                ? paddingBetweenColumns
                                : paddingToRightEdge) + cellPadding,
                        height: rowHeight,
                        // @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'MinWidth<string | number> | undefined'.
                        minInlineSize: column.minWidth,
                        // @ts-expect-error - TS2322 - Type 'number | null | undefined' is not assignable to type 'MaxWidth<string | number> | undefined'.
                        maxWidth: column.maxWidth,
                        ...rowStyle,
                    }}
                    className={css(
                        styles.td,
                        topAlignCellContent
                            ? styles.tdVerticalAlignTop
                            : styles.tdVerticalAlignMiddle,
                        columnIndex === 0 && hideFirstColumn && styles.hidden,
                        column.dropOnMobile && styles.droppedOnMobile,
                        column.hiddenOnPrint && styles.hiddenOnPrint,
                    )}
                >
                    <div
                        className={css(styles.tableBodyCell)}
                        ref={(div) => {
                            if (columnIndex === 0 && hideFirstColumn) {
                                return;
                            }
                            cells[columnIndex] = cells[columnIndex] || [];
                            // @ts-expect-error - TS2322 - Type 'HTMLDivElement | null' is not assignable to type 'HTMLDivElement'.
                            cells[columnIndex][rowIndex + 1] = div;
                        }}
                    >
                        {emptyRow ? (
                            column.emptyPlaceHolder
                        ) : (
                            <TableBodyCell
                                column={column}
                                row={row}
                                hovered={rowIndex === hoveredRow}
                                disabled={!!rowDisabled}
                                rowIndex={rowIndex}
                            />
                        )}
                    </div>
                </td>
            ))}
        </tr>
    );
};

TableRow.defaultProps = {
    backgroundStyle: "default",
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component -- moved an old file
export default class SortableTable extends React.Component<
    SortableTableProps,
    SortableTableState
> {
    container: HTMLDivElement | null | undefined;
    cells: Array<Array<HTMLDivElement>>;
    rows: Array<Array<HTMLTableRowElement>>;
    headingRows: Array<HTMLTableRowElement>;

    static defaultProps: {
        calculatePaddingDynamically: boolean;
        hasTopAndBottomBorders: boolean;
        rowHeight: RowHeight;
        topAlignCellContent: boolean;
        useCursorPointerOnHover: boolean;
    } = {
        rowHeight: 48,
        hasTopAndBottomBorders: true,
        useCursorPointerOnHover: true,
        calculatePaddingDynamically: true,
        topAlignCellContent: false,
    };

    constructor(props: SortableTableProps) {
        super(props);

        const {children, defaultSortColumn, defaultSortDirection} = props;
        const hasColumns =
            // @ts-expect-error - TS2345 - Argument of type 'Column | ReactElement<ColumnProps, string | JSXElementConstructor<any>> | ReactElement<ColumnProps, string | JSXElementConstructor<...>>[] | undefined' is not assignable to parameter of type 'ReactNode | ReactNode[]'.
            React.Children.toArray(children).filter(
                // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                (child) => child.type === Column,
            ).length > 0;

        let activeSortColumn = null;
        let activeSortDirection = null;
        if (hasColumns && defaultSortColumn) {
            // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
            activeSortColumn = defaultSortColumn;
            // @ts-expect-error - TS2322 - Type 'Sort' is not assignable to type 'null'.
            activeSortDirection = defaultSortDirection || "ASC";
        }

        this.state = {
            activeSortColumn,
            activeSortDirection,
            hoveredRow: null,
            paddingBetweenColumns: 0,
            paddingToRightEdge: 0,
            freezeFirstColumn: false,
            rowHeights: null,
            headingRowHeight: null,
        };

        this.cells = [];
        this.rows = [];
        this.headingRows = [];
    }

    componentDidMount() {
        this.mounted = true;
        window.addEventListener("resize", this.maybeAdjustPaddingOrRowHeights);

        // Wait for Aphrodite styles to be applied
        // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
        // eslint-disable-next-line no-restricted-syntax -- moved an old file
        setTimeout(this.maybeAdjustPaddingOrRowHeights, 0);
    }

    UNSAFE_componentWillReceiveProps(nextProps: SortableTableProps) {
        // If defaultSortColumn or defaultSortDirection is changed, that means
        // we updated the default sort of the table. In that case, we want
        // to update the active sort state.
        const sortProps: Partial<SortableTableState> = {};
        if (this.props.defaultSortColumn !== nextProps.defaultSortColumn) {
            sortProps.activeSortColumn = nextProps.defaultSortColumn;
            sortProps.activeSortDirection =
                nextProps.defaultSortDirection || "ASC";
        } else if (
            this.props.defaultSortDirection !== nextProps.defaultSortDirection
        ) {
            sortProps.activeSortDirection = nextProps.defaultSortDirection;
        }

        // @ts-expect-error - TS2345 - Argument of type '{ activeSortColumn?: DescriptorId | null | undefined; activeSortDirection?: Sort | null | undefined; hoveredRow?: number | null | undefined; paddingBetweenColumns?: number | undefined; paddingToRightEdge?: number | undefined; freezeFirstColumn?: boolean | undefined; rowHeights?: number[] | ... 1 more ... | undefined...' is not assignable to parameter of type 'SortableTableState | ((prevState: Readonly<SortableTableState>, props: Readonly<SortableTableProps>) => SortableTableState | ... 1 more ... | null) | Pick<...> | null'.
        this.setState({...sortProps});
    }

    componentDidUpdate(
        prevProps: SortableTableProps,
        prevState: SortableTableState,
    ) {
        // Maybe adjust padding (to keep columns aligned properly in the
        // container) or row heights (if there's a frozen column, to keep the
        // rows in the two tables aligned).
        const {
            hoveredRow,
            paddingBetweenColumns,
            paddingToRightEdge,
            freezeFirstColumn,
            rowHeights,
            headingRowHeight,
        } = this.state;

        const hoveredRowChanged = hoveredRow !== prevState.hoveredRow;
        const rowHeightsChanged = !this.rowHeightsEquals(
            rowHeights,
            prevState.rowHeights,
        );
        const paddingChanged =
            prevState.paddingBetweenColumns !== paddingBetweenColumns ||
            prevState.paddingToRightEdge !== paddingToRightEdge ||
            prevState.freezeFirstColumn !== freezeFirstColumn;

        const headingRowHeightChanged =
            prevState.headingRowHeight !== headingRowHeight;

        if (hoveredRowChanged || rowHeightsChanged || headingRowHeightChanged) {
            // A different hovered row does not change size of cells, and row
            // heights should be the last thing to be adjusted, so if either
            // change, we don't need to readjust padding or rowHeights.
            return;
        }

        if (paddingChanged && freezeFirstColumn) {
            // We recently changed the padding, which means some cells may be
            // different sizes than before. If we have a frozen column, then
            // maybe adjust row heights. Otherwise, we only have one table, so
            // there's no need to calculate row heights, and rowHeights is
            // already null from the call to maybeAdjustPaddingOrRowHeights, so
            // we don't need to update state.
            this.maybeAdjustRowHeights();
        } else if (!paddingChanged) {
            // Something changed other than hoveredRow, rowHeights, or padding-
            // most likely table content. Check if we need to adjust the
            // padding or row heights.
            // TODO(jeff, WEB-1378): Use Wonder Blocks Timing API.
            // eslint-disable-next-line no-restricted-syntax -- moved an old file
            setTimeout(this.maybeAdjustPaddingOrRowHeights, 0);
        }
    }

    componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener(
            "resize",
            this.maybeAdjustPaddingOrRowHeights,
        );
    }

    mounted = false;

    maybeAdjustPaddingOrRowHeights: () => void = () => {
        const {rowHeights, headingRowHeight} = this.state;

        if (!this.mounted) {
            return;
        }

        if (rowHeights || typeof headingRowHeight === "number") {
            // Null out rowHeights so that current row heights are not used to
            // calculate rowHeights. This is needed when row heights should
            // shrink, like when resizing to a bigger screen.
            this.setState({rowHeights: null, headingRowHeight: null});
        }
        const newPadding = this.recalculatePadding();
        if (newPadding) {
            // If we need to adjust padding, then do so and trigger
            // maybeAdjustRowHeights from componentDidUpdate.
            this.setState(newPadding);
        } else {
            // There aren't any updates to padding or freezeFirstColumn on this
            // render cycle, so we'll call maybeAdjustRowHeights immediately to
            // adjust row heights if necessary.
            this.maybeAdjustRowHeights();
        }
    };

    recalculatePadding():
        | undefined
        | {
              freezeFirstColumn: boolean;
              paddingBetweenColumns: number;
              paddingToRightEdge: number;
          } {
        if (
            !this.container ||
            !this.cells ||
            !this.props.calculatePaddingDynamically
        ) {
            return;
        }

        // The way an HTML table handls spacing is simple:
        // It makes sure that each column is wide enough for its content,
        // then evenly distributes the remaining width to each column.
        // This behavior isn't quite what we want - it makes it impossible
        // to evenly space out adjacent columns with different alignments.
        // We fix this by calculating how wide each column should really be,
        // then manually setting the horizontal padding on th/td elements to
        // take up the remaining space.
        //
        // Before:  ([  column 1  ])([  column 2  ])([  column 3  ])
        // After:   ([column 1]      )([column 2]      )([column 3])
        //           ^^^^^^^^^^ content          ^^^^^^^ padding
        let containerWidth = this.container.offsetWidth;
        if (this.state.freezeFirstColumn) {
            // Account for <div className={css(styles.spacer)}/>
            containerWidth =
                containerWidth -
                (window.innerWidth >= 768
                    ? 20 - cellPadding
                    : 16 - cellPadding);
        }

        const columnWidths = this.cells.map((column) => {
            const widths = column.map((cell) => (cell ? cell.offsetWidth : 0));
            return Math.max(...widths.filter((num) => num != null));
        });
        const numberOfColumns = columnWidths.length;
        const sumOfColumnWidths = columnWidths.reduce((a, b) => a + b, 0);
        // Calculate how much padding the table has after accounting
        // for the built-in padding within each cell.
        const paddingToDistribute =
            containerWidth -
            sumOfColumnWidths -
            2 * cellPadding * numberOfColumns;

        let paddingBetweenColumns = 0;
        let paddingToRightEdge = 0;
        let freezeFirstColumn = false;

        if (numberOfColumns > 1 && paddingToDistribute > 0) {
            // Distribute the padding evenly
            paddingBetweenColumns = Math.ceil(
                paddingToDistribute / (numberOfColumns - 1),
            );

            if (paddingBetweenColumns >= maxPaddingBetweenColumns) {
                // We don't want too much padding between columns - it doesn't
                // look good, and it makes the table hard to scan. So we cap
                // it and put the remainder between the last column and the
                // table's right edge.
                paddingBetweenColumns = maxPaddingBetweenColumns;
                paddingToRightEdge =
                    paddingToDistribute -
                    (numberOfColumns - 1) * maxPaddingBetweenColumns;
            }
        } else if (numberOfColumns > 1 && paddingToDistribute < 0) {
            // There isn't enough room to display all of the table's content,
            // so freeze the first column and let the other columns scroll.
            freezeFirstColumn = true;
        }

        if (
            paddingBetweenColumns !== this.state.paddingBetweenColumns ||
            paddingToRightEdge !== this.state.paddingToRightEdge ||
            freezeFirstColumn !== this.state.freezeFirstColumn
        ) {
            return {
                paddingBetweenColumns,
                paddingToRightEdge,
                freezeFirstColumn,
            };
        }
    }

    rowHeightsEquals(
        prev?: Array<number> | null,
        cur?: Array<number> | null,
    ): boolean {
        if (prev === cur) {
            return true;
        }

        if (!prev || !cur || prev.length !== cur.length) {
            return false;
        }

        for (let i = 0; i < prev.length; ++i) {
            if (prev[i] !== cur[i]) {
                return false;
            }
        }
        return true;
    }

    maybeAdjustRowHeights() {
        // If there are table data, the table should have
        // this.rows = [first column rows, all columns rows]
        // so we could say there's table rows rendered in the table by
        // comparing this.rows.length to 2.
        const hasRows = this.rows.length === 2;
        // although headingRows is not dependant on the table data (since we
        // always render heading rows) but it has the same structure,
        // this.headingRows = [first column, all columns]
        const hasHeadingRows = this.headingRows.length === 2;

        // Don't do anything when the component is not mounted or there's no
        // rows (including headers) at all.
        if (!this.mounted || (!hasRows && !hasHeadingRows)) {
            return;
        }

        const {freezeFirstColumn, rowHeights, headingRowHeight} = this.state;
        const {data, maxRows, pageSize} = this.props;

        if (!freezeFirstColumn) {
            // If the first column is not frozen, the data is displayed in one
            // table, which will auto-adjust the rows' heights, so we don't
            // need to explicitly adjust them. rowHeights should be null so
            // we don't set height to be larger than necessary.
            if (rowHeights || typeof headingRowHeight === "number") {
                // Only set state if rowHeights is not already null, to prevent
                // another trigger of the component lifecycle.
                this.setState({rowHeights: null, headingRowHeight: null});
            }
            return;
        }

        if (hasRows) {
            const [leftTable, rightTable] = this.rows;
            const rowCount = maxRows
                ? Math.min(data.length, maxRows)
                : pageSize
                  ? Math.min(data.length, pageSize)
                  : data.length;

            const newRowHeights = Array(rowCount)
                // @ts-expect-error - TS2554 - Expected 1-3 arguments, but got 0.
                .fill()
                .map((_, i) =>
                    Math.max(
                        leftTable[i].offsetHeight,
                        rightTable[i].offsetHeight,
                    ),
                );

            const rowHeightsChanged = !this.rowHeightsEquals(
                rowHeights,
                newRowHeights,
            );

            if (rowHeightsChanged) {
                this.setState({rowHeights: newRowHeights});
            }
        }

        if (hasHeadingRows) {
            const [leftHeader, rightHeader] = this.headingRows;

            const newHeaderHeight = Math.max(
                leftHeader.offsetHeight,
                rightHeader.offsetHeight,
            );

            if (headingRowHeight !== newHeaderHeight) {
                this.setState({headingRowHeight: newHeaderHeight});
            }
        }
    }

    getColumns(): Array<ColumnDescriptor> {
        return (
            // @ts-expect-error - TS2345 - Argument of type 'Column | ReactElement<ColumnProps, string | JSXElementConstructor<any>> | ReactElement<ColumnProps, string | JSXElementConstructor<...>>[] | undefined' is not assignable to parameter of type 'ReactNode | ReactNode[]'.
            React.Children.toArray(this.props.children)
                // @ts-expect-error - TS2339 - Property 'type' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                .filter((child) => child.type === Column)
                .map((child, columnIndex) =>
                    // @ts-expect-error - TS2339 - Property 'props' does not exist on type 'string | number | ReactElement<any, string | JSXElementConstructor<any>> | ReactFragment | ReactPortal'.
                    transformColumnProps(child.props, columnIndex),
                )
        );
    }

    handleHeaderClick({id, sortKeyFn, onSort}: ColumnDescriptor) {
        const {activeSortColumn, activeSortDirection} = this.state;

        if (!sortKeyFn && !onSort) {
            // We don't know how to sort by this column
            return;
        }

        let direction;
        if (activeSortColumn === id) {
            direction = activeSortDirection === "ASC" ? "DESC" : "ASC";
        } else {
            direction = "ASC";
        }

        if (onSort) {
            onSort(direction);
        }

        this.setState({
            activeSortColumn: id,
            activeSortDirection: direction,
        });
    }

    renderTable(
        data: Array<Row>,
        columns: Array<ColumnDescriptor>,
        isFrozenFirstColumn?: boolean,
    ): null | React.ReactElement<React.ComponentProps<"table">> {
        const {
            rowHeight,
            tableWidth,
            onRowClick,
            isRowDisabled,
            hasTopAndBottomBorders,
            tableHeadingRenderer,
            useCursorPointerOnHover,
            alternatingBackgroundVariant,
            useTallHeading,
            useMultipleLineColumnLabel,
            topAlignCellContent,
            rowStyles,
        } = this.props;

        const {
            activeSortColumn,
            activeSortDirection,
            hoveredRow,
            paddingBetweenColumns,
            paddingToRightEdge,
            freezeFirstColumn,
            rowHeights,
            headingRowHeight,
        } = this.state;

        if (isFrozenFirstColumn && !freezeFirstColumn) {
            return null;
        }

        const hasHeaders = columns.some((column) => column.label);
        const hideFirstColumn = !isFrozenFirstColumn && freezeFirstColumn;
        const topAndBottomBorder =
            hasTopAndBottomBorders && styles.bottomBorder;
        const hasEmptyPlaceHolder = columns.some(
            (column) => column.emptyPlaceHolder,
        );

        const tableHeadingRow = (
            <tr
                className={css(
                    styles.tr,
                    alternatingBackgroundVariant && styles.offWhiteBackground,
                )}
                ref={(tr) => {
                    if (!tr) {
                        return;
                    }

                    const tableIndex = hideFirstColumn ? 1 : 0;
                    this.headingRows[tableIndex] =
                        this.headingRows[tableIndex] || [];
                    this.headingRows[tableIndex] = tr;
                }}
                style={
                    typeof headingRowHeight === "number"
                        ? {height: headingRowHeight}
                        : {}
                }
            >
                {hasHeaders &&
                    columns.map((column, columnIndex) => {
                        // Note(tamarab): Using "td" for empty header cells fixes an a11y concern as empty "th" cells are an issue for screenreaders
                        const HeaderTag = column.label ? "th" : "td";
                        return (
                            <HeaderTag
                                key={columnIndex}
                                style={{
                                    textAlign: column.align,
                                    paddingInlineEnd:
                                        (columnIndex < columns.length - 1
                                            ? paddingBetweenColumns
                                            : paddingToRightEdge) + cellPadding,
                                    // The alternating variant has more top-padding,
                                    // unless useTallHeading is explicitly set to false
                                    // since it has an offwhite background and feels
                                    // a bit claustrophobic with only 5px top
                                    // padding.
                                    paddingBlockStart:
                                        (useTallHeading ??
                                        alternatingBackgroundVariant)
                                            ? 20
                                            : 5,
                                }}
                                className={css(
                                    styles.th,
                                    columnIndex === 0 &&
                                        hideFirstColumn &&
                                        styles.hidden,
                                    column.dropOnMobile &&
                                        styles.droppedOnMobile,
                                    column.hiddenOnPrint &&
                                        styles.hiddenOnPrint,
                                )}
                            >
                                <div
                                    className={css(styles.tableHeaderCell)}
                                    ref={(div) => {
                                        if (
                                            columnIndex === 0 &&
                                            hideFirstColumn
                                        ) {
                                            return;
                                        }
                                        this.cells[columnIndex] =
                                            this.cells[columnIndex] || [];
                                        // @ts-expect-error - TS2322 - Type 'HTMLDivElement | null' is not assignable to type 'HTMLDivElement'.
                                        this.cells[columnIndex][0] = div;
                                    }}
                                >
                                    <TableHeaderCell
                                        column={column}
                                        columnIndex={columnIndex}
                                        activeSortColumn={activeSortColumn}
                                        activeSortDirection={
                                            activeSortDirection
                                        }
                                        useMultipleLineColumnLabel={
                                            useMultipleLineColumnLabel
                                        }
                                        onClick={() =>
                                            this.handleHeaderClick(column)
                                        }
                                    />
                                </div>
                            </HeaderTag>
                        );
                    })}
            </tr>
        );

        return (
            <table
                className={css(styles.table, topAndBottomBorder)}
                style={{width: tableWidth}}
            >
                {tableHeadingRenderer ? (
                    tableHeadingRenderer(tableHeadingRow)
                ) : (
                    <thead className={css(topAndBottomBorder)}>
                        {tableHeadingRow}
                    </thead>
                )}
                <tbody>
                    {data.length === 0 && hasEmptyPlaceHolder && (
                        <TableRow
                            row={{}}
                            rowIndex={0}
                            rowHeight={rowHeight}
                            hoveredRow={hoveredRow}
                            columns={columns}
                            cells={this.cells}
                            paddingBetweenColumns={paddingBetweenColumns}
                            paddingToRightEdge={paddingToRightEdge}
                            hideFirstColumn={hideFirstColumn}
                            emptyRow={true}
                            rows={this.rows}
                        />
                    )}
                    {data.map((row, rowIndex) => {
                        let actualRowHeight: undefined | RowHeight | number =
                            rowHeight;
                        if (rowHeights && rowHeights.length > rowIndex) {
                            actualRowHeight = rowHeight
                                ? Math.max(rowHeights[rowIndex], rowHeight)
                                : rowHeights[rowIndex];
                        }

                        const isOddRow = rowIndex % 2 === 0;

                        return (
                            <TableRow
                                key={rowIndex}
                                row={row}
                                rowIndex={rowIndex}
                                rowHeight={actualRowHeight}
                                hoveredRow={hoveredRow}
                                onRowClick={onRowClick}
                                isRowDisabled={isRowDisabled}
                                useCursorPointerOnHover={
                                    useCursorPointerOnHover
                                }
                                columns={columns}
                                onMouseEnter={() =>
                                    this.setState({hoveredRow: rowIndex})
                                }
                                onMouseLeave={() =>
                                    this.setState({hoveredRow: null})
                                }
                                cells={this.cells}
                                paddingBetweenColumns={paddingBetweenColumns}
                                paddingToRightEdge={paddingToRightEdge}
                                hideFirstColumn={hideFirstColumn}
                                backgroundStyle={
                                    alternatingBackgroundVariant && !isOddRow
                                        ? "offwhite"
                                        : "default"
                                }
                                rowStyle={rowStyles && rowStyles(rowIndex)}
                                rows={this.rows}
                                topAlignCellContent={topAlignCellContent}
                            />
                        );
                    })}
                </tbody>
            </table>
        );
    }

    render(): React.ReactNode {
        const {activeSortColumn, activeSortDirection, freezeFirstColumn} =
            this.state;
        const {maxRows, pageSize, pageIndex, hasFixedTopRow} = this.props;
        const columns = this.getColumns();

        // Make a copy of data since we don't want the sort function to mutate props
        let data = this.props.data.slice();

        // Get the active sort function
        let sortKeyFn;
        if (activeSortColumn != null) {
            for (const column of columns) {
                // When the active sort column has onSort prop, we don't want
                // to perform the client side sort.
                if (column.id === activeSortColumn && !column.onSort) {
                    sortKeyFn = column.sortKeyFn;
                }
            }
        }

        // Apply the active sort function (we capture as a const so flow
        // knows this is a fixed thing at this point).
        const foundSortKeyFn = sortKeyFn;
        if (foundSortKeyFn) {
            const totalRow = data[0];
            if (hasFixedTopRow) {
                data.shift();
            }

            data = data.sort((a, b) => {
                let aResult = foundSortKeyFn(a, activeSortDirection);
                let bResult = foundSortKeyFn(b, activeSortDirection);

                // strings should always be compared by lowercase otherwise
                // Z would come before a
                if (typeof aResult === "string") {
                    aResult = aResult.toLowerCase();
                }

                if (typeof bResult === "string") {
                    bResult = bResult.toLowerCase();
                }

                // TypeScript complains when we try to compare a possible-string to
                // a possible-number. In reality, we never actually do this:
                // a student's "name" column will always be a string, whereas
                // an assignment's "score" column will always be a number.
                // TypeScript doesn't know this, though, and so it raises a false
                // positive.
                return aResult > bResult ? 1 : -1;
            });

            if (activeSortDirection === "DESC") {
                data = data.reverse();
            }

            if (hasFixedTopRow) {
                data.unshift(totalRow);
            }
        }

        if (maxRows && maxRows < data.length) {
            data = data.slice(0, this.props.maxRows);
        } else if (pageSize && pageSize < data.length) {
            const start = (pageIndex || 0) * pageSize;
            data = data.slice(start, start + pageSize);
        }

        return (
            <div
                ref={(container) => (this.container = container)}
                className={`box-sizing-border-box-reset ${css(
                    styles.container,
                    !freezeFirstColumn && styles.containerUnfrozen,
                )}`}
            >
                {freezeFirstColumn && (
                    <div className={css(styles.frozenFirstColumn)}>
                        {this.renderTable(
                            data,
                            columns.slice(0, 1),
                            /* isFrozenFirstColumn */ true,
                        )}
                        <div className={css(styles.shadow)} />
                    </div>
                )}
                <div
                    className={css(
                        styles.allColumns,
                        freezeFirstColumn && styles.allColumnsScroll,
                    )}
                >
                    {this.renderTable(data, columns)}
                    {freezeFirstColumn && (
                        <div className={css(styles.spacer)} />
                    )}
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    // Base table element styles. Note that we do not override 'display' on
    // any of these elements, which ensures that screenreaders treat these as
    // data tables.
    table: {
        borderCollapse: "collapse",
        paddingBlockEnd: 1,
    },
    tr: {
        borderBlockEnd: `1px solid ${color.offBlack16}`,
        ":last-child": {
            borderBottom: "none",
        },
    },
    bottomBorder: {
        borderBlockEnd: `1px solid ${color.offBlack16}`,
    },
    hoveredRow: {
        background: color.offWhite,
    },
    hoverWithPointer: {
        cursor: "pointer",
    },
    selectedRow: {
        background: color.offWhite,
    },
    th: {
        verticalAlign: "bottom",
        paddingBlockEnd: 5,
        paddingInlineStart: cellPadding,
        // paddingRight and paddingTop are set dynamically
    },
    td: {
        fontFamily: "inherit",
        fontSize: 15,
        lineHeight: "22px",
        color: color.offBlack,
        paddingInlineStart: cellPadding,
        // paddingRight is set dynamically
    },
    tdVerticalAlignMiddle: {
        verticalAlign: "middle",
        paddingBlockStart: 0,
        paddingBlockEnd: 0,
    },
    tdVerticalAlignTop: {
        verticalAlign: "top",
        boxSizing: "border-box",
        paddingBlockStart: spacing.small_12,
        paddingBlockEnd: spacing.small_12,
    },
    offWhiteBackground: {
        backgroundColor: color.offWhite,
    },
    // The cells we measure in adjustPadding() must grow to fit their contents.
    tableHeaderCell: {
        display: "inline-block",
    },
    tableBodyCell: {
        display: "inline-block",
    },

    // Responsive styles
    container: {
        display: "flex",
        alignItems: "flex-start",

        [mediaQueries.smOrSmaller]: {
            marginInlineStart: 16 - cellPadding,
        },
        [mediaQueries.mdOrLarger]: {
            marginInlineStart: 20 - cellPadding,
        },
    },
    containerUnfrozen: {
        [mediaQueries.smOrSmaller]: {
            marginInlineEnd: 16 - cellPadding,
        },
        [mediaQueries.mdOrLarger]: {
            marginInlineEnd: 20 - cellPadding,
        },
    },
    frozenFirstColumn: {
        position: "relative",
        borderInlineEnd: `1px solid ${color.offBlack16}`,
    },
    allColumns: {
        flexGrow: 1,
        display: "flex",
    },
    allColumnsScroll: {
        overflowX: "scroll",
    },
    spacer: {
        [mediaQueries.smOrSmaller]: {
            flex: `0 0 ${16 - cellPadding}px`,
        },
        [mediaQueries.mdOrLarger]: {
            flex: `0 0 ${20 - cellPadding}px`,
        },
    },
    shadow: {
        position: "absolute",
        top: 1,
        bottom: 1,
        insetInlineEnd: 0,
        boxShadow: "2px 0px 6px 1px rgba(0, 0, 0, 0.4)",
    },

    // Misc styles
    hidden: {
        display: "none",
    },
    droppedOnMobile: {
        [mediaQueries.smOrSmaller]: {
            display: "none",
        },
    },
    hiddenOnPrint: {
        "@media print": {
            display: "none",
        },
    },
});
